import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Rating, Stack, TextField,
  Typography, GridLegacy as Grid,
} from '@mui/material';
import { Download, CheckCircle } from '@mui/icons-material';
import { internshipService } from '../services/api';
import { InternshipDetail, InternshipReview, STATUS_META, PROGRESS_META, reviewLabel, fmtDate, apiError } from './internshipTypes';
import { downloadInternshipCertificate } from './internshipCertificatePdf';

// The intern's own view: progress through the internship, goals, check-ins (write the
// self-assessment, read feedback the mentor has shared), the final outcome and certificate.
// Scores, recommendations and HR remarks are never sent to this view by the API.
const MyInternship: React.FC = () => {
  const [detail, setDetail] = useState<InternshipDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setDetail(await internshipService.getMine());
    } catch (err) {
      setError(apiError(err, 'Failed to load your internship.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!detail) return <Box sx={{ p: 2 }}><Alert severity="info">You are not enrolled in an internship.</Alert></Box>;

  const isOpen = !['Converted', 'Completed', 'Terminated'].includes(detail.status);
  const total = new Date(detail.plannedEndDate).getTime() - new Date(detail.startDate).getTime();
  const elapsed = Math.min(Math.max(Date.now() - new Date(detail.startDate).getTime(), 0), total);
  const pct = total > 0 ? Math.round((elapsed / total) * 100) : 0;
  const finalDecision = [...detail.decisions].reverse().find(d => d.decision);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" gutterBottom>My Internship</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6">{detail.roleTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {detail.department ? `${detail.department} · ` : ''}Mentor: {detail.mentorName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fmtDate(detail.startDate)} – {fmtDate(detail.actualEndDate ?? detail.plannedEndDate)}
            </Typography>
          </Box>
          <Box>
            <Chip label={STATUS_META[detail.status]?.label ?? detail.status} color={STATUS_META[detail.status]?.color ?? 'default'} />
          </Box>
        </Stack>
        {isOpen && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">{pct}% complete</Typography>
              <Typography variant="caption">{detail.daysRemaining} days remaining</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Final evaluation opens on {fmtDate(detail.finalWindowOpensOn)}. Please submit your final self-assessment before its due date.
            </Typography>
          </Box>
        )}
        {finalDecision && (
          <Alert severity={finalDecision.decision === 'Terminate' ? 'warning' : 'success'} sx={{ mt: 2 }}>
            {finalDecision.decision === 'Confirm' && `Congratulations! You are confirmed as ${finalDecision.conversionDesignation}${finalDecision.conversionEmploymentType === 'Probation' ? ' (on probation)' : ''} from ${fmtDate(finalDecision.effectiveDate)}.`}
            {finalDecision.decision === 'Extend' && `Your internship has been extended by ${finalDecision.extensionMonths} month(s) until ${fmtDate(finalDecision.newEndDate)}.`}
            {finalDecision.decision === 'Complete' && `Your internship is complete. Last working day: ${fmtDate(finalDecision.effectiveDate)}.`}
            {finalDecision.decision === 'Terminate' && `Your internship ends on ${fmtDate(finalDecision.effectiveDate)}.`}
          </Alert>
        )}
      </Paper>

      {detail.certificates.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Certificate</Typography>
          {detail.certificates.map(c => (
            <Stack key={c.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Typography variant="body2">{c.certificateType} certificate · No. {c.certificateNo} · issued {fmtDate(c.issuedAt)}</Typography>
              <Button variant="contained" startIcon={<Download />} onClick={() => downloadInternshipCertificate(c)}>Download</Button>
            </Stack>
          ))}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            The certificate was also emailed to you, with a verification link that keeps working after your account is closed.
          </Typography>
        </Paper>
      )}

      <Typography variant="h6" gutterBottom>Goals</Typography>
      {detail.goals.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>Your mentor hasn't set goals yet.</Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {detail.goals.map(g => (
            <Grid item xs={12} md={6} key={g.id}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">{g.title}</Typography>
                  <Chip size="small" label={g.status} />
                </Stack>
                <Typography variant="caption" color="text.secondary">Due {fmtDate(g.endDate)} · weight {g.weightage}%</Typography>
                <LinearProgress variant="determinate" value={g.progress} sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                <Typography variant="caption">{g.progress}%</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h6" gutterBottom>Check-ins & feedback</Typography>
      <Stack spacing={1.5}>
        {detail.reviews.map(r => (
          <ReviewCard key={r.id} detail={detail} review={r} onSubmitted={(d, label) => { setDetail(d); setMessage(`${label}: self-assessment submitted.`); }} onError={setError} />
        ))}
      </Stack>
    </Box>
  );
};

const ReviewCard: React.FC<{
  detail: InternshipDetail;
  review: InternshipReview;
  onSubmitted: (d: InternshipDetail, label: string) => void;
  onError: (msg: string) => void;
}> = ({ detail, review: r, onSubmitted, onError }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const feedbackVisible = r.mentorFeedback !== null;

  const submit = async () => {
    setSaving(true);
    try {
      onSubmitted(await internshipService.submitSelfAssessment(detail.id, r.id, text), reviewLabel(r));
      setText('');
    } catch (err) {
      onError(apiError(err, 'Failed to submit self-assessment.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2">{reviewLabel(r)}</Typography>
          <Typography variant="caption" color="text.secondary">Due {fmtDate(r.dueDate)}</Typography>
        </Box>
        {feedbackVisible ? <Chip size="small" color="success" icon={<CheckCircle />} label="Feedback received" /> : <Chip size="small" label={r.status === 'Cancelled' ? 'Cancelled' : 'Awaiting feedback'} />}
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Your self-assessment</Typography>
        {r.selfAssessment ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.selfAssessment}</Typography>
        ) : r.canSubmitSelf ? (
          <>
            <TextField fullWidth multiline minRows={3} size="small" sx={{ mt: 0.5 }}
              placeholder="What did you work on, what did you learn, where do you need support?"
              value={text} onChange={e => setText(e.target.value)} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Visible to your mentor and HR once submitted. Cannot be edited afterwards.</Typography>
              <Button size="small" variant="contained" disabled={!text.trim() || saving} onClick={submit}>Submit</Button>
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary"><i>Not submitted</i></Typography>
        )}
      </Box>

      {feedbackVisible && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Mentor feedback — {r.reviewedByName}</Typography>
            {r.progressStatus && <Chip size="small" variant="outlined" label={PROGRESS_META[r.progressStatus]?.label} color={PROGRESS_META[r.progressStatus]?.color} />}
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.mentorFeedback}</Typography>
          {r.strengths && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Strengths:</b> {r.strengths}</Typography>}
          {r.improvements && <Typography variant="body2"><b>Areas to improve:</b> {r.improvements}</Typography>}
          {r.mentorRatings && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mt: 1 }}>
              {detail.competencies.map(c => (
                <Box key={c.key}>
                  <Typography variant="caption" display="block">{c.label}</Typography>
                  <Rating size="small" readOnly value={r.mentorRatings?.[c.key] ?? 0} />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default MyInternship;
